/* eslint-disable no-empty */
"use client";

import React from "react";
import type { ErrorInfo } from "react";
import { ErrorPageContent } from "@/components/shared/ErrorPage";
import { Card, CardContent } from "@/components/ui/card";
import { getLastCorrelationIdSnapshot } from "@/contexts/CorrelationContext";
import { generateClientErrorId } from "@/hooks/error/useErrorPage";
import * as Sentry from "@sentry/nextjs";

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  error?: Error;
};

type EnhancedError = Error & { correlationId?: string; digest?: string };

export class AppErrorBoundary extends React.Component<Props, State> {
  // Stable client error ID generated once per error boundary instance
  private clientErrorId: string | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  private getClientErrorId(): string {
    if (!this.clientErrorId) {
      this.clientErrorId = generateClientErrorId();
    }
    return this.clientErrorId;
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const enhancedError = error as EnhancedError;
    const correlationId = enhancedError.correlationId ?? getLastCorrelationIdSnapshot() ?? enhancedError.digest ?? null;
    const identifier = correlationId ?? this.getClientErrorId();

    Sentry.captureException(error, {
      tags: {
        ...(correlationId ? { correlationId } : {}),
        ...(enhancedError.digest ? { errorDigest: enhancedError.digest } : {}),
        errorIdentifier: identifier,
      },
      contexts: {
        correlation: {
          ...(correlationId ? { id: correlationId } : {}),
          ...(enhancedError.digest ? { digest: enhancedError.digest } : {}),
          identifier,
        },
        error: {
          message: error.message,
          name: error.name,
        },
        react: {
          componentStack: info?.componentStack,
        },
      },
    });
  }

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const error = this.state.error;
    const enhancedError = (error as EnhancedError) ?? new Error("Unknown error");
    const correlationId = enhancedError.correlationId ?? getLastCorrelationIdSnapshot() ?? enhancedError.digest ?? null;
    // Use client-generated ID as fallback when no server-side identifier exists
    const identifier = correlationId ?? this.getClientErrorId();

    return (
      <div className="fixed inset-0 z-modal-critical flex items-center justify-center bg-cede-bg-primary p-4">
        <Card className="w-full max-w-md border-container shadow-xl mx-auto">
          <CardContent className="pt-6">
            <ErrorPageContent error={enhancedError} supportId={identifier} showErrorDetails={true} />
          </CardContent>
        </Card>
      </div>
    );
  }
}
